import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./analisiDrawdownBacktestAndamentoPortafoglio.module.scss";
import { formatLocalizedDate } from "utils/dateUtils";
import { formatItalianNumber } from "utils/formatUtils";

type ComponentProps = { data: any; data2?: any };

const AnalisiDrawdownBacktestAndamentoPortafoglio: React.FC<ComponentProps> = ({
  data,
  data2,
}) => {
  //** drawdow chart ( second area ) */
  const drawdownData = data.analisi_backtest.analisi_drawdown;
  const formattedData = drawdownData.map((item) => ({
    ...item,
    value: item.value * 1, // Convert to percentage
  }));
  const { minValueDrawdown, maxValueDrawdown, yAxisTicks } = useMemo(() => {
    // const { yAxisTicks } = useMemo(() => {
    const values = formattedData.map((item) => item.value);
    const min = Math.floor(Math.min(...values) / 5) * 5; // Round down to nearest 5
    const max = Math.ceil(Math.max(...values) / 5) * 5; // Round up to nearest 5

    // Generate ticks every 5%
    const ticks = [];
    for (let i = min; i <= max; i += 5) {
      ticks.push(i);
    }

    const valueRange = max - min;
    let tickStep = 10;
    if (valueRange < 50) tickStep = 5;
    if (valueRange < 20) tickStep = 2;
    if (valueRange < 10) tickStep = 1;

    const yAxisTicks = Array.from(
      { length: Math.floor((max - min) / tickStep) + 1 },
      (_, i) => min + i * tickStep,
    );

    return {
      minValueDrawdown: min,
      maxValueDrawdown: max,
      yAxisTicks: yAxisTicks,
    };
  }, [formattedData]);

  const { t, i18n } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  if (!data) {
    return null;
  }

  // Extract required data
  const portfolioData = data.analisi_backtest.simulazione_andamento_portafoglio;

  const downsample = (portfolioData, interval = 50) => {
    return portfolioData.filter((_, index) => index % interval === 0);
  };

  const downsampledData = downsample(portfolioData, 1);

  const maxDrawdownStats =
    data.analisi_backtest.analisi_drawdown_max_drawdown_stats;
  const recoveryTimeStats =
    data.analisi_backtest.analisi_drawdown_recovery_time_stats;

  // Check if there's a drawdown
  const hasDrawdown = maxDrawdownStats && maxDrawdownStats[1]?.weight !== null;

  // Get drawdown dates
  const drawdownStartDate = hasDrawdown
    ? new Date(maxDrawdownStats[1]?.weight)
    : null;
  const drawdownEndDate = hasDrawdown
    ? new Date(maxDrawdownStats[2]?.weight)
    : null;

  const hasRecovery =
    hasDrawdown && recoveryTimeStats && recoveryTimeStats[2]?.weight !== null;

  const recoveryEndDate = hasRecovery
    ? new Date(recoveryTimeStats[2]?.weight)
    : null;

  const processedData = downsampledData.map((point) => ({
    ...point,
    originalDate: point.date,
    value: parseFloat(point.value),
  }));

  // Sort data by date
  const sortedData = [...processedData].sort(
    (a, b) =>
      new Date(a.originalDate).getTime() - new Date(b.originalDate).getTime(),
  );

  // Prepare the unified dataset with overlapping boundary points for continuity
  const chartData = sortedData.map((point) => {
    const pointDate = new Date(point.originalDate);

    // Initialize all segment values as null
    const dataPoint = {
      ...point,
      normalValue: null,
      drawdownValue: null,
      recoveryValue: null,
      normalAfterValue: null,
    };

    // Determine which segment this point belongs to with boundary overlap for continuity
    if (hasDrawdown) {
      if (pointDate < drawdownStartDate) {
        dataPoint.normalValue = point.value;
      } else if (pointDate.getTime() === drawdownStartDate.getTime()) {
        // Boundary: Include in both normal and drawdown for continuity
        dataPoint.normalValue = point.value;
        dataPoint.drawdownValue = point.value;
      } else if (
        pointDate > drawdownStartDate &&
        pointDate < drawdownEndDate
      ) {
        dataPoint.drawdownValue = point.value;
      } else if (pointDate.getTime() === drawdownEndDate.getTime()) {
        // Boundary: Include in both drawdown and recovery (if exists) for continuity
        dataPoint.drawdownValue = point.value;
        if (hasRecovery) {
          dataPoint.recoveryValue = point.value;
        } else {
          dataPoint.normalAfterValue = point.value;
        }
      } else if (
        hasRecovery &&
        pointDate > drawdownEndDate &&
        pointDate < recoveryEndDate
      ) {
        dataPoint.recoveryValue = point.value;
      } else if (hasRecovery && pointDate.getTime() === recoveryEndDate.getTime()) {
        // Boundary: Include in both recovery and normalAfter for continuity
        dataPoint.recoveryValue = point.value;
        dataPoint.normalAfterValue = point.value;
      } else {
        dataPoint.normalAfterValue = point.value;
      }
    } else {
      // If no drawdown, everything is normal period
      dataPoint.normalValue = point.value;
    }

    return dataPoint;
  });

  // Italian month abbreviations
  const ITALIAN_MONTHS: { [key: number]: string } = {
    0: "Gen", 1: "Feb", 2: "Mar", 3: "Apr", 4: "Mag", 5: "Giu",
    6: "Lug", 7: "Ago", 8: "Set", 9: "Ott", 10: "Nov", 11: "Dic"
  };

  // Smart axis configuration based on data timeline
  const getSmartAxisConfig = (data: typeof sortedData) => {
    if (!data.length) return { ticks: [], tickFormatter: () => "" };

    // Count unique years and months
    const uniqueYears = new Set<number>();
    const uniqueMonths = new Set<string>();

    data.forEach((point) => {
      const date = new Date(point.originalDate);
      uniqueYears.add(date.getFullYear());
      uniqueMonths.add(`${date.getFullYear()}-${date.getMonth()}`);
    });

    const numYears = uniqueYears.size;
    const numMonths = uniqueMonths.size;

    // Strategy: >= 5 years → yearly ticks, < 5 years → 7 evenly spaced ticks
    if (numYears >= 5) {
      // Find year boundary indices and dates
      const ticks: string[] = [];
      let prevYear: number | null = null;

      data.forEach((point) => {
        const year = new Date(point.originalDate).getFullYear();
        if (prevYear !== year) {
          ticks.push(point.originalDate);
          prevYear = year;
        }
      });

      return {
        ticks,
        tickFormatter: (value: string) => {
          return new Date(value).getFullYear().toString();
        }
      };
    } else {
      // < 5 years: 7 evenly spaced labels
      const includeDay = numMonths < 5;
      let tickIndices: number[];

      if (data.length <= 1) {
        tickIndices = [0];
      } else if (data.length < 7) {
        tickIndices = Array.from({ length: data.length }, (_, i) => i);
      } else {
        const step = (data.length - 1) / 6;
        tickIndices = Array.from({ length: 7 }, (_, i) => Math.round(i * step));
      }

      const ticks = tickIndices.map(i => data[i].originalDate);

      return {
        ticks,
        tickFormatter: (value: string) => {
          const date = new Date(value);
          const month = ITALIAN_MONTHS[date.getMonth()];
          const year = date.getFullYear();

          if (includeDay) {
            return `${date.getDate()} ${month} ${year}`;
          }
          return `${month} ${year}`;
        }
      };
    }
  };

  const { ticks: xAxisTicks, tickFormatter: xAxisTickFormatter } = getSmartAxisConfig(sortedData);

  const chartCopy = {
    fontSize: 11,
    fill: colorsConfig.finecoBlack,
    fontFamily: "Arial",
  };

  // Calculate Y-axis range with simple step-based logic (matching Python backend)
  const allValues = processedData.map((d) => d.value);
  const actualMin = Math.min(...allValues);
  const actualMax = Math.max(...allValues);

  // Determine step size based on range
  let step = 20;
  if (actualMin > 90 && actualMax < 110) {
    step = 5;
  }

  const adjustedMin = Math.floor(actualMin / step) * step;
  const adjustedMax = Math.ceil(actualMax / step) * step;

  // Generate Y-axis ticks
  const yAxisTicksPortfolio = Array.from(
    { length: (adjustedMax - adjustedMin) / step + 1 },
    (_, i) => adjustedMin + i * step
  );

  // Custom tooltip for handling different segment values
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Find the first non-null payload entry
      const validEntry = payload.find((entry) => entry.value !== null);
      if (!validEntry) return null;

      const originalDate = new Date(validEntry.payload.originalDate);
      const formattedDate = formatLocalizedDate(originalDate, i18n.language);

      // Determine which segment is active at this point
      let segmentColor = colorsConfig.FinecoBluePrimary;
      let segmentValue = null;

      if (validEntry.payload.drawdownValue !== null) {
        segmentColor = "#FF0000"; // Red
        segmentValue = validEntry.payload.drawdownValue;
      } else if (validEntry.payload.recoveryValue !== null) {
        segmentColor = "#008000"; // Green
        segmentValue = validEntry.payload.recoveryValue;
      } else if (validEntry.payload.normalValue !== null) {
        segmentValue = validEntry.payload.normalValue;
      } else if (validEntry.payload.normalAfterValue !== null) {
        segmentValue = validEntry.payload.normalAfterValue;
      }

      if (segmentValue === null) return null;

      return (
        <div
          className={styles.tooltipContainer}
          style={{
            backgroundColor: "white",
            border: `1px solid ${colorsConfig.FinecoGrayLight1}`,
            padding: "8px 10px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <p className={styles.tooltipLabel} style={{ margin: "0 0 5px 0" }}>
            {formattedDate}
          </p>
          <p
            style={{
              color: segmentColor,
              margin: "3px 0",
              fontSize: "12px",
            }}
          >
            {`${t("portfolio.value")}: ${formatItalianNumber(segmentValue)}`}
          </p>
        </div>
      );
    }
    return null;
  };
  const CustomTooltipDrawdown = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const segmentValue = payload[0].value;
      const segmentDate = payload[0].payload.date;
      const segmentColor = colorsConfig.FinecoBluePrimary;

      const originalDate = new Date(segmentDate);
      const formattedDate = formatLocalizedDate(originalDate, i18n.language);
      return (
        <div
          className={styles.tooltipContainer}
          style={{
            backgroundColor: "white",
            border: `1px solid ${colorsConfig.FinecoGrayLight1}`,
            padding: "8px 10px",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <p className={styles.tooltipLabel} style={{ margin: "0 0 5px 0" }}>
            {formattedDate}
          </p>
          <p
            style={{
              color: segmentColor,
              margin: "3px 0",
              fontSize: "12px",
            }}
          >
            {`${t("portfolio.value")}: ${formatItalianNumber(segmentValue)}%`}
          </p>
        </div>
      );
    }
    return null;
  };

  if (data.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.chartContainer}>
        <div className={styles.chartContainerInner}>
          <ResponsiveContainer width="100%" height={300} debounce={500}>
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 40, left: -25, bottom: 10 }}
            >
              <CartesianGrid
                strokeWidth={1}
                stroke={colorsConfig.GrayLight2}
                strokeOpacity={1}
                horizontal={true}
                vertical={false}
              />

              <XAxis
                dataKey="originalDate"
                ticks={xAxisTicks}
                tickFormatter={xAxisTickFormatter}
                dx={10}
                dy={5}
                textAnchor="middle"
                height={40}
                tick={chartCopy}
                tickLine={false}
                axisLine={{ stroke: colorsConfig.GrayLight2 }}
              />
              <YAxis
                tick={chartCopy}
                domain={[adjustedMin, adjustedMax]}
                ticks={yAxisTicksPortfolio}
                tickLine={false}
                tickFormatter={(value) => Math.round(value).toString()}
                axisLine={{ stroke: colorsConfig.GrayLight2 }}
              />
              <Tooltip content={(props) => <CustomTooltip {...props} />} />
              <Line
                type="linear"
                dataKey={() => 100}
                stroke={colorsConfig.FinecoGrayLight1}
                strokeDasharray="3 3"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />

              <Line
                name={t("portfolio.normalPeriod") || "Normal Period"}
                type="linear"
                dataKey="normalValue"
                stroke={colorsConfig.FinecoBluePrimary}
                dot={false}
                strokeWidth={2}
                strokeOpacity={1}
                activeDot={{ r: 5 }}
                connectNulls={true}
                isAnimationActive={false}
              />

              {/* Drawdown period */}
              <Line
                name={t("portfolio.drawdownPeriod") || "Drawdown Period"}
                type="linear"
                dataKey="drawdownValue"
                stroke={colorsConfig.red}
                dot={false}
                strokeWidth={2}
                strokeOpacity={1}
                activeDot={{ r: 5 }}
                connectNulls={true}
                isAnimationActive={false}
              />

              {/* Recovery period */}
              <Line
                name={t("portfolio.recoveryPeriod") || "Recovery Period"}
                type="linear"
                dataKey="recoveryValue"
                stroke={colorsConfig.Green}
                dot={false}
                strokeWidth={2}
                strokeOpacity={1}
                activeDot={{ r: 5 }}
                connectNulls={true}
                isAnimationActive={false}
              />

              {/* Normal period after recovery */}
              <Line
                name={t("portfolio.normalPeriod") || "Normal Period"}
                type="linear"
                dataKey="normalAfterValue"
                stroke={colorsConfig.FinecoBluePrimary}
                dot={false}
                strokeWidth={2}
                strokeOpacity={1}
                activeDot={{ r: 5 }}
                connectNulls={true}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className={styles.chartContainerInner}>
          <ResponsiveContainer width="100%" height={300} debounce={500}>
            <AreaChart
              data={formattedData}
              margin={{ top: 20, right: 40, left: -25, bottom: 0 }}
            >
              <defs>
                <linearGradient id="gradientFill" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor="rgb(255, 255, 255)"
                    stopOpacity={1}
                  />
                  <stop
                    offset="80%"
                    stopColor={colorsConfig.BlueLight}
                    stopOpacity={1}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeWidth={1}
                stroke={colorsConfig.GrayLight2}
                strokeOpacity={1}
                horizontal={true}
                vertical={false}
              />
              <XAxis
                dataKey="date"
                ticks={xAxisTicks}
                tickFormatter={xAxisTickFormatter}
                dx={10}
                dy={10}
                textAnchor="middle"
                height={40}
                tick={chartCopy}
                tickLine={false}
                axisLine={{ stroke: colorsConfig.GrayLight2 }}
              />
              <YAxis
                tick={chartCopy}
                tickFormatter={(value) => `${value}`}
                domain={[minValueDrawdown, maxValueDrawdown]}
                ticks={yAxisTicks}
                axisLine={{ stroke: colorsConfig.GrayLight2 }}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltipDrawdown />} />
              {/* <Legend
              content={<CustomizedLegend />}
              verticalAlign="bottom"
              height={0}
            /> */}
              <Area
                type="monotone"
                dataKey="value"
                stroke={colorsConfig.BlueLight}
                fill="url(#gradientFill)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AnalisiDrawdownBacktestAndamentoPortafoglio;
